/**
 * Image capture service — native camera on iOS/Android, `<input type="file">` on web.
 *
 * WHY THIS EXISTS (Card 20, task B). Every upload surface in the app used a plain
 * `<input type="file" accept="image/*">`. In a browser that input's "Take Photo" option
 * IS the camera, so the app has always claimed a camera capability it delegated entirely
 * to the WebView. On iOS that delegation was also the bug fixed in task A: WKWebView
 * routes the file input through the system camera/photo picker, and iOS terminates the
 * process outright if the app has no `NSCameraUsageDescription` /
 * `NSPhotoLibraryUsageDescription`. Both strings now exist (ios/App/App/Info.plist), but
 * a file input is still the WebView's camera, not the app's — which is exactly the shape
 * App Review reads as "a repackaged website" under Guideline 4.2.
 *
 * So on native this goes through @capacitor/camera: a real UIImagePickerController /
 * Android camera intent, owned by the app, with the app's own permission prompts.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not replace the file input on web, and it
 * does not replace the *multi-file / video / document* picker on the referral and
 * message surfaces — @capacitor/camera returns exactly one image. Those surfaces keep
 * their input for picking and gain a separate native "Take Photo" action. See
 * `src/docs/NATIVE_CAPABILITY_EVIDENCE.md`.
 *
 * SHAPE OF THE RESULT. Callers already have working validation/compression pipelines
 * keyed on `File`, so this returns a `File` indistinguishable from one the file input
 * would have produced — real bytes, a real name, a canonical `image/*` MIME. Nothing
 * downstream needs to know where it came from.
 *
 * NOTE: `useCamera.ts` used to exist and was deleted as dead code. This is not a
 * resurrection of it; it is written against the six call sites that exist today.
 */

import { isNative } from '@/utils/platform';

/** Where the image comes from. */
export type CaptureSource =
  /** Straight to the camera — used for explicit "Take Photo" actions. */
  | 'camera'
  /** Native action sheet letting the user choose camera or photo library. */
  | 'prompt';

export interface CaptureImageOptions {
  source?: CaptureSource;
  /** JPEG quality 0-100. Defaults to 90 — high enough that the existing 5MB/25MB
   *  compression paths downstream are the only thing that ever degrades an image. */
  quality?: number;
}

/**
 * Why a capture produced no file. `cancelled` is the ordinary case (the user backed
 * out) and must never surface as an error; `denied` is the one that needs a message,
 * because on both platforms a second `getPhoto()` will not re-prompt — only Settings
 * can undo it.
 */
export type CaptureFailureReason = 'cancelled' | 'denied' | 'unavailable';

export class CaptureError extends Error {
  readonly reason: CaptureFailureReason;

  constructor(reason: CaptureFailureReason, message: string) {
    super(message);
    this.name = 'CaptureError';
    this.reason = reason;
  }
}

/**
 * Whether a native camera is available. `false` on web, where callers must keep using
 * their file input — the point of this service is to branch, not to paper over.
 */
export function isNativeCaptureAvailable(): boolean {
  return isNative;
}

/** Extension for the filename we invent, since a native capture has no original name. */
const FORMAT_TO_EXTENSION: Record<string, string> = {
  jpeg: 'jpg',
  jpg: 'jpg',
  png: 'png',
  gif: 'gif',
  webp: 'webp',
  heic: 'heic',
  heif: 'heif',
};

/**
 * Map a plugin rejection onto a reason. The Camera plugin communicates cancellation and
 * permission refusal through the error *message* rather than a code, and the wording
 * differs per platform, so this matches on substrings and defaults to `cancelled` —
 * the benign reading. Treating an unrecognised failure as a denial would put a
 * "go to Settings" message in front of users whose permissions are perfectly fine.
 */
function classifyCaptureError(error: unknown): CaptureFailureReason {
  const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();

  if (message.includes('denied') || message.includes('permission') || message.includes('not authorized')) {
    return 'denied';
  }
  if (message.includes('not implemented') || message.includes('unavailable') || message.includes('not available')) {
    return 'unavailable';
  }
  return 'cancelled';
}

function messageForReason(reason: CaptureFailureReason): string {
  switch (reason) {
    case 'denied':
      return 'Trade Loop does not have permission to use the camera or your photos. Turn it on in your device Settings under Trade Loop, then try again.';
    case 'unavailable':
      return 'The camera is not available on this device.';
    case 'cancelled':
      return 'Capture cancelled.';
  }
}

/**
 * Capture or pick a single image natively.
 *
 * Resolves to `null` when the user cancels — the overwhelmingly common non-success path,
 * and one that should leave the UI exactly as it was. Throws `CaptureError` for the two
 * cases that need telling the user about: a denied permission and an unavailable camera.
 *
 * Throws if called on web. Callers must gate on `isNativeCaptureAvailable()`; failing
 * loudly here is better than silently doing nothing on the one platform where the file
 * input is still the right answer.
 */
export async function captureImage(options: CaptureImageOptions = {}): Promise<File | null> {
  if (!isNative) {
    throw new CaptureError('unavailable', 'Native capture is only available in the mobile app.');
  }

  const { source = 'prompt', quality = 90 } = options;

  // Imported lazily so the plugin's JS never lands in the web bundle's critical path —
  // on web this module is only ever reached for `isNativeCaptureAvailable()`.
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');

  let photo;
  try {
    photo = await Camera.getPhoto({
      quality,
      // Uri + fetch rather than Base64: a full-resolution phone photo is several MB, and
      // base64 inflates it by a third in JS memory before we can make a Blob of it.
      resultType: CameraResultType.Uri,
      source: source === 'camera' ? CameraSource.Camera : CameraSource.Prompt,
      // The picker's own crop UI is a second, unnecessary step — the surfaces here crop
      // and compress on their own terms.
      allowEditing: false,
      // Without this, photos taken in landscape arrive rotated: the bytes are portrait
      // with an EXIF orientation flag that <img> honours and canvas compression does not.
      correctOrientation: true,
      saveToGallery: false,
    });
  } catch (error) {
    const reason = classifyCaptureError(error);
    if (reason === 'cancelled') return null;
    throw new CaptureError(reason, messageForReason(reason));
  }

  const uri = photo.webPath ?? photo.path;
  if (!uri) return null;

  const response = await fetch(uri);
  const blob = await response.blob();

  const format = (photo.format || 'jpeg').toLowerCase();
  const extension = FORMAT_TO_EXTENSION[format] ?? 'jpg';
  // `blob.type` is normally correct here, but a WebView that serves the capacitor file
  // URL without a Content-Type yields an empty string, and an empty MIME fails both our
  // client allow-lists and the server's multipart check.
  const mimeType = blob.type || `image/${format === 'jpg' ? 'jpeg' : format}`;

  return new File([blob], `photo-${Date.now()}.${extension}`, {
    type: mimeType,
    lastModified: Date.now(),
  });
}

/**
 * `captureImage` wrapped for the common UI shape: hand it a file consumer and an error
 * reporter and it does the cancel/deny bookkeeping. Keeps six call sites from each
 * writing the same try/catch.
 */
export async function captureImageInto(
  onFile: (file: File) => void | Promise<void>,
  onError: (title: string, message: string) => void,
  options: CaptureImageOptions = {},
): Promise<void> {
  try {
    const file = await captureImage(options);
    if (file) await onFile(file);
  } catch (error) {
    if (error instanceof CaptureError) {
      onError(error.reason === 'denied' ? 'Permission Needed' : 'Camera Unavailable', error.message);
      return;
    }
    console.error('[mediaCapture] Capture failed:', error);
    onError('Capture Failed', 'Could not read the photo. Please try again.');
  }
}
